import { v4 as uuidv4 } from 'uuid';
import { HandleType } from '@/enums/handle-type.enum';
import { ModelType } from '@/enums/model-type.enum';
import { getHandleId, getNodeTemplates } from '@/components/Nodes/Utils';
import { FAL_ASPECT_RATIO_LABEL_MODELS } from '@/components/Nodes/ModelComponents/consts/model-image-size.const';
import { SEEDANCE_25_MODELS } from '@/components/Nodes/ModelComponents/consts/seedance-25.const';
import { FLUX_3_DEFAULT_DURATION, FLUX_3_VIDEO_MODEL } from '@/components/Nodes/ModelComponents/consts/flux-3.const';
import { NodeDefinition } from '@/types/api/nodeDefinition';
import { removeBlockedAnyLlmModelsIfNeeded } from '@/utils/ModelProviders/blockedAnyLlmModels.utils';
import { UndoRedoEntry } from '../../undoRedo';
import { createNewNode } from '../../useGraph';
import { type FlowGraph } from '../../FlowGraph';
import { getParameterTranslation } from './parameters.utils';
import type { NodeId, ParameterValue } from 'web';
import type { AiModelData } from '@/types/nodes/model';
import type { CustomGlslData, NodeDataWithParams, ParameterInfo, Schema } from '@/types/node';

// Luma Ray 3.2 video edit: when auto_controls is on, the model derives the full conditioning
// schedule and the manual strength / per-signal controls are ignored by the backend, so hide them.
const LUMA_VIDEO_EDIT_MANUAL_KEYS = [
  'strength',
  'pose_enabled',
  'pose_strength',
  'depth_enabled',
  'depth_blur',
  'normals_enabled',
  'normals_augmentation',
  'trajectory_enabled',
  'trajectory_sparsity',
  'face_enabled',
];

// Each per-signal control is a checkbox plus a dependent tuning input that only applies when the
// checkbox is on. Map dependent key -> controlling checkbox so the tuning input can be disabled
// while its checkbox is off (the backend ignores it in that case anyway).
const LUMA_VIDEO_EDIT_CONTROL_DEPENDENCIES: Record<string, string> = {
  pose_strength: 'pose_enabled',
  depth_blur: 'depth_enabled',
  normals_augmentation: 'normals_enabled',
  trajectory_sparsity: 'trajectory_enabled',
};

const getDurationExposureConfig = (key: string, modelName?: string) => {
  if (key !== 'duration' || !modelName) return undefined;
  if (SEEDANCE_25_MODELS.has(modelName)) return { min: 4, max: 30, defaultValue: 10 };
  if (modelName === FLUX_3_VIDEO_MODEL) return { min: 5, max: 20, defaultValue: FLUX_3_DEFAULT_DURATION };
  return undefined;
};

export class ParameterView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  getParameterIds(): string[] {
    return this.graph.wasm.call((wasm) => wasm.parameterIds(this.nodeId)) ?? [];
  }

  getParameterInfo(key: string): ParameterInfo | undefined {
    const { title, description } = this.getParameterTitleAndDescription(key);
    const constraint = this.graph.wasm.call((wasm) => wasm.parameterConstraint(this.nodeId, key)) ?? {
      type: 'string',
    };
    return {
      ...constraint,
      title,
      description,
    };
  }

  isParameterExposed(key: string): boolean {
    return this.graph.wasm.call((wasm) => wasm.isParameterExposed(this.nodeId, key)) ?? false;
  }

  /**
   * Whether a parameter's inline control should be disabled: exposed parameters are driven by a
   * connected input node, and model-specific rules (e.g. Luma per-signal tuning inputs) may gate it.
   */
  isParameterDisabled(key: string): boolean {
    return this.isParameterExposed(key) || this.isLumaControlDisabled(key);
  }

  getParameterValue(key: string): ParameterValue | undefined {
    return this.graph.wasm.call((wasm) => wasm.evaluateParameter(this.nodeId, key));
  }

  setParameterValue(key: string, newValue: ParameterValue, ongoing: boolean = false) {
    this.graph.edit(() => this.storeParameterValueAndGetUndoEntry(key, newValue), ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
  }

  // Caller must already be inside `flowGraph.edit` and is responsible for the returned UndoRedoEntry.
  storeParameterValueAndGetUndoEntry(key: string, newValue: ParameterValue): UndoRedoEntry {
    const undoEntry = this.graph.store.updateNodeData<NodeDataWithParams>(this.nodeId, (prevData) => ({
      params: {
        ...prevData.params,
        [key]: convertParameterValueToLegacyValue(newValue),
      },
    }));
    undoEntry.add(this.graph.wasmChange((wasm) => wasm.setParameterValue(this.nodeId, key, newValue)));
    return undoEntry;
  }

  exposeParameter(key: string, nodeTypes: NodeDefinition[]) {
    this.graph.edit(() => {
      const { params, schema, model } = this.graph.store.getNodeData<NodeDataWithParams>(this.nodeId);

      const sanitizedSchema = { ...schema };
      const durationConfig = getDurationExposureConfig(key, model?.name);
      if (durationConfig) {
        if (this.getParameterValue(key)?.value === 'auto') return UndoRedoEntry.empty();
        sanitizedSchema[key] = {
          ...schema[key],
          type: 'integer',
          min: durationConfig.min,
          max: durationConfig.max,
          step: 1,
        };
      }
      if (this.isAnyLlmLikeModel() && sanitizedSchema.model) {
        sanitizedSchema.model.options = removeBlockedAnyLlmModelsIfNeeded(sanitizedSchema.model.options ?? []);
      }

      const undoEntry = UndoRedoEntry.empty();
      if (durationConfig) {
        undoEntry.add(
          this.graph.wasmChange((wasm) =>
            wasm.setWildcardParameterConstraint(
              this.nodeId,
              key,
              { type: 'integer_with_limits', min: durationConfig.min, max: durationConfig.max },
              { type: 'integer', value: durationConfig.defaultValue },
            ),
          ),
        );
        undoEntry.add(
          this.graph.store.updateNodeData<NodeDataWithParams>(this.nodeId, () => ({ schema: sanitizedSchema })),
        );
      }

      // custom_glsl keeps its parameters as wasm-native `fields` rather than the legacy schema map.
      // Image sizes do have a schema entry, but its options drift from the model's real ones
      // (ChatGPT Images 2.0 stores 3 of the 9 it accepts), so both read the constraint from wasm.
      const isCustomGlsl = this.graph.getNodeType(this.nodeId) === 'custom_glsl';
      const useWasmConstraint = isCustomGlsl || this.getParameterInfo(key)?.type === 'image_size';
      const propertySchema = useWasmConstraint ? this.parameterInfoToSchema(key) : sanitizedSchema[key];
      const wasmValue = useWasmConstraint ? this.getParameterValue(key) : undefined;
      const paramValue: unknown = wasmValue ? convertParameterValueToLegacyValue(wasmValue) : params[key];

      if (typeof propertySchema !== 'object') throw new Error(`${key} not present in sanitizedSchema`);

      // 1. create and add handle
      undoEntry.add(
        this.graph.addNodeInputHandle(this.nodeId, key, {
          required: propertySchema.required,
          description: propertySchema.description,
          format: propertySchema.type || 'text',
          order: propertySchema.order,
          type: getHandleType(propertySchema.type),
          id: uuidv4(),
        }),
      );

      // 2. create new node with params as values, connect
      const newNodesData = getNodeTemplates(propertySchema.type, paramValue, propertySchema);
      if (newNodesData) {
        const originNode = this.graph.store.getNode(this.nodeId);
        const position = {
          x: originNode.position.x - 550,
          y: originNode.position.y + 50,
        };

        const newNode = createNewNode(newNodesData.template.id, nodeTypes, position, newNodesData.template.initialData);
        if (newNode) {
          if (newNode.type === 'muxv2') {
            const newNodeData = newNode.data as NodeDataWithParams;
            newNodeData.schema['options'].exposed = false;
          }
          const sourceHandleId = getHandleId(newNode.id, 'output', newNodesData.handleName);
          const targetHandleId = getHandleId(this.nodeId, 'input', key);

          undoEntry.add(this.graph.addNodes([newNode]));
          this.graph.updateNodeOutputs(newNode.id, false);
          const newEdge = this.graph.createEdge(newNode.id, this.nodeId, sourceHandleId, targetHandleId);
          undoEntry.add(this.graph.addEdges([newEdge]));
        }
      } else {
        undoEntry.add(this.graph.wasmChange((wasm) => wasm.setNodeInput(this.nodeId, key, null)));
      }

      // 3. disable the property in the dynamic fields. custom_glsl tracks exposure in wasm
      // (the field's Parameter becomes an Input when the edge connects), so the legacy schema
      // flag is both unnecessary and would write a stray schema entry onto the node.
      if (!isCustomGlsl) {
        undoEntry.add(setSchemaFieldExposed(this.graph, this.nodeId, key, true));
      }

      return undoEntry;
    });
  }

  collapseParameter(key: string) {
    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();

      const value = this.graph.wasm.call((wasm) => wasm.evaluateValidParameter(this.nodeId, key));
      const { model, schema } = this.graph.store.getNodeData<NodeDataWithParams>(this.nodeId);
      const durationConfig = getDurationExposureConfig(key, model?.name);

      // 1. remove connected node if it still exists and has no other connections
      const targetHandleId = getHandleId(this.nodeId, 'input', key);
      const edges = this.graph.store.getEdges();
      const edgeToRemove = edges.find((e) => e.targetHandle === targetHandleId);
      if (edgeToRemove) {
        const nodeToRemove = edgeToRemove.source;
        const nodeHasOtherEdges = edges.some(
          (e) => e.id !== edgeToRemove.id && (e.source === nodeToRemove || e.target === nodeToRemove),
        );
        if (nodeHasOtherEdges) {
          undoEntry.add(this.graph.removeEdges([edgeToRemove.id]));
        } else {
          undoEntry.add(this.graph.removeNodes([nodeToRemove]));
        }
      }

      // 2. remove handle for the property
      undoEntry.add(this.graph.removeNodeInputHandle(this.nodeId, key));

      // 3. enable property in dynamic fields. custom_glsl tracks exposure in wasm, so skip the
      // legacy schema flag (see exposeParameter).
      if (this.graph.getNodeType(this.nodeId) !== 'custom_glsl') {
        undoEntry.add(setSchemaFieldExposed(this.graph, this.nodeId, key, false));
      }
      if (durationConfig) {
        undoEntry.add(
          this.graph.wasmChange((wasm) =>
            wasm.setWildcardParameterConstraint(
              this.nodeId,
              key,
              { type: 'enum', options: schema[key].options ?? [] },
              { type: 'string', value: String(durationConfig.defaultValue) },
            ),
          ),
        );
        undoEntry.add(
          this.graph.store.updateNodeData<NodeDataWithParams>(this.nodeId, () => ({
            schema: { ...schema, [key]: { ...schema[key], type: 'enum', exposed: false } },
          })),
        );
      }

      // 4. set parameter value
      if (value !== undefined) {
        undoEntry.add(
          this.storeParameterValueAndGetUndoEntry(
            key,
            durationConfig && (value.type === 'integer' || value.type === 'float')
              ? { type: 'string', value: String(value.value) }
              : value,
          ),
        );
      }

      return undoEntry;
    });
    this.graph.updateNodeOutputs(this.nodeId);
  }

  /**
   * Build a legacy {@link Schema} from the wasm constraint ({@link getParameterInfo}) so the shared
   * expose flow (handle creation + upstream node template) can run off it. Used for custom_glsl,
   * which has no schema map, and for image sizes, whose stored schema options are unreliable.
   */
  private parameterInfoToSchema(key: string): Schema | undefined {
    const info = this.getParameterInfo(key);
    if (info === undefined) {
      return undefined;
    }
    const order = this.getParameterIds().indexOf(key);
    const base = { title: info.title, description: info.description, order: Math.max(order, 0), required: false };
    switch (info.type) {
      case 'integer':
        return { ...base, type: 'integer' };
      case 'integer_with_limits':
        return { ...base, type: 'integer', min: info.min, max: info.max };
      case 'float':
        return { ...base, type: 'number' };
      case 'float_with_limits':
        return { ...base, type: 'number', min: info.min, max: info.max };
      case 'boolean':
        return { ...base, type: 'boolean' };
      case 'point':
        throw new Error(`Parameter "${key}" is a point and cannot be exposed as an input`);
      case 'point_radius':
        throw new Error(`Parameter "${key}" is a point_radius and cannot be exposed as an input`);
      case 'angle_radius':
        throw new Error(`Parameter "${key}" is an angle_radius and cannot be exposed as an input`);
      case 'line':
        throw new Error(`Parameter "${key}" is a line and cannot be exposed as an input`);
      case 'color':
        throw new Error(`Parameter "${key}" is a color and cannot be exposed as an input`);
      case 'enum':
        return { ...base, type: 'enum', options: info.options };
      case 'image_size':
        return { ...base, type: 'fal_image_size', options: info.options };
      default:
        return { ...base, type: 'number' };
    }
  }

  /**
   * any_llm, prompt_enhancer, image_describer all use any_llm model "type"
   */
  public isAnyLlmLikeModel(): boolean {
    const nodeType = this.graph.getNodeType(this.nodeId);
    const nodeData = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    if (nodeType === 'custommodelV2') {
      const type = nodeData.kind.type;
      return type === 'any_llm' || type === 'prompt_enhancer' || type === 'image_describer';
    }
    return false;
  }

  public isTopazImageUpscale(): boolean {
    return this.graph.getModelNodeType(this.nodeId) === 'topaz_image_upscale';
  }

  public isGptImage2(): boolean {
    // gpt image 2 is a subset of the gpt_image1 node type
    if (this.graph.getModelNodeType(this.nodeId) !== 'gpt_image1') return false;

    const modelValue = this.getParameterValue('model');
    return modelValue?.type === 'string' && modelValue.value === 'GPT Image 2';
  }

  public isGptImageModel(): boolean {
    // Generate is a code-defined kind (gpt_image1); edit stays a wildcard, so also match by model name.
    if (this.graph.getModelNodeType(this.nodeId) === 'gpt_image1') return true;
    const nodeData = this.graph.store.getNodeData<NodeDataWithParams>(this.nodeId);
    return nodeData.model?.name === ModelType.GptImage1 || nodeData.model?.name === ModelType.GptImage1Edit;
  }

  public usesFalAspectRatioLabels(): boolean {
    const nodeData = this.graph.store.getNodeData<NodeDataWithParams>(this.nodeId);
    return FAL_ASPECT_RATIO_LABEL_MODELS.has(nodeData.model?.name ?? '');
  }

  public isLumaVideoEdit(): boolean {
    const nodeData = this.graph.store.getNodeData<NodeDataWithParams>(this.nodeId);
    return nodeData.model?.name === ModelType.LumaRay32VideoEdit;
  }

  /**
   * Luma video edit: hide the manual edit controls while auto_controls is enabled. Mirror the
   * backend predicate (auto is on unless explicitly false) so the UI never shows manual controls
   * the server would ignore.
   */
  public isLumaManualControlHidden(key: string): boolean {
    if (!this.isLumaVideoEdit() || !LUMA_VIDEO_EDIT_MANUAL_KEYS.includes(key)) {
      return false;
    }
    // Auto is on unless explicitly false; resolve via the graph (evaluateParameter) so an exposed/
    // connected auto_controls input is respected rather than the possibly-stale stored param.
    return this.getBooleanParameterValue('auto_controls') !== false;
  }

  /**
   * Luma video edit: disable a per-signal tuning input while its controlling checkbox is off.
   * Internal detail of {@link isParameterDisabled}.
   */
  private isLumaControlDisabled(key: string): boolean {
    if (!this.isLumaVideoEdit()) {
      return false;
    }
    const controllingKey = LUMA_VIDEO_EDIT_CONTROL_DEPENDENCIES[key];
    if (controllingKey === undefined) {
      return false;
    }
    // Resolve via the graph so an exposed/connected checkbox input is respected (consistent with
    // how the field values themselves render).
    return this.getBooleanParameterValue(controllingKey) !== true;
  }

  private getBooleanParameterValue(key: string): boolean | undefined {
    const value = this.getParameterValue(key);
    return value?.type === 'boolean' ? value.value : undefined;
  }

  private getParameterTitleAndDescription(parameterKey: string): { title: string; description: string } {
    const nodeType = this.graph.getNodeType(this.nodeId);

    switch (nodeType) {
      case 'custommodelV2': {
        const modelKind = this.graph.store.getNodeData<AiModelData>(this.nodeId).kind;
        if (modelKind.type === 'wildcard') {
          // If wildcard, use the title and description from the data. Note there's no translation for the user imported models!
          const parameter = modelKind.parameters.find(([schema, _]) => schema.id === parameterKey)?.[0];
          return {
            title: parameter?.title ?? '',
            description: parameter?.description ?? '',
          };
        }
        return getParameterTranslation(nodeType, parameterKey, modelKind.type);
      }
      case 'muxv2':
        return getParameterTranslation(nodeType, parameterKey);
      case 'media_iterator':
        // media iterator doesn't have any parameters to display
        // we are returning a default title and description to prevent errors (https://linear.app/weavy/issue/WEA-2453)
        return {
          title: '',
          description: '',
        };
      case 'masks':
        return getParameterTranslation(nodeType, parameterKey);
      case 'custom_glsl': {
        const descriptor = this.graph.store
          .getNodeDataNullable<CustomGlslData>(this.nodeId)
          ?.fields.find(([field]) => field.id === parameterKey)?.[0];
        return { title: descriptor?.label ?? parameterKey, description: '' };
      }
    }
    throw new Error(`Invalid parameter ${parameterKey} for node type ${nodeType}`);
  }
}

export function convertParameterValueToLegacyValue(newValue: ParameterValue): any {
  let legacyValue: any = newValue.value;
  if (newValue.type === 'image_size') {
    if (newValue.value.type === 'custom') {
      legacyValue = { width: newValue.value.width, height: newValue.value.height };
    } else if (newValue.value.value === 'match_input') {
      legacyValue = null;
    } else {
      legacyValue = newValue.value.value;
    }
  }
  return legacyValue;
}

function getHandleType(type: Schema['type'] | undefined): HandleType {
  switch (type) {
    case 'integer':
    case 'input':
    case 'input-integer':
    case 'input-float':
    case 'input-number':
    case 'number':
      return HandleType.Number;
    case 'string':
    case 'text':
      return HandleType.Text;
    case 'seed':
      return HandleType.Seed;
    case 'array':
      return HandleType.Array;
    case 'enum':
    case 'fal_image_size':
      return HandleType.Text;
    case 'boolean':
      return HandleType.Boolean;
    default: {
      return HandleType.Any;
    }
  }
}

function setSchemaFieldExposed(flowGraph: FlowGraph, nodeId: NodeId, key: string, exposed: boolean): UndoRedoEntry {
  const { schema } = flowGraph.store.getNodeData<NodeDataWithParams>(nodeId);
  return flowGraph.store.updateNodeData<NodeDataWithParams>(nodeId, () => ({
    schema: {
      ...schema,
      [key]: {
        ...schema[key],
        exposed,
      },
    },
  }));
}
